import { html, TemplateResult, unsafeCSS } from 'lit';
import { query, customElement, state } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_SANDBOX_ENDPOINT_SENSOR } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { AnalysisMode, ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  NetworkSandboxingModuleFormValue,
  SandboxEndpointSensorSearchableKeywords,
} from '../../../../utils/uiSettings/sandbox/sandbox.types';
import {
  getNetworkSandboxingDefaultValue,
  getSandboxEndpointSensorSearchableKeywords,
  getRemediationActionConfigsByAnalysisMode,
} from '../../../../utils/uiSettings/sandbox/sandbox.values';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { Feature } from '@nirvana/shared/constants';
import {
  ManualSubmissionApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { GzLocalSandboxInstanceWebComponent } from '../../common/localSandbox';
import { GzProxyConfigurationWebComponent } from '../../common/proxyConfiguration';
import { GzContentPrefilteringWebComponent } from '../../common/contentPrefiltering';
import {
  convertDbValueToFormValue,
  convertFormValueToDbValue,
} from '../../common/contentPrefiltering/component/complex-selection-group-config';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { GzDetonationProfileWebComponent } from '../../common/detonationProfile';
import { getTitleForLocalSandbox } from '../../common/localSandbox/utils';
import { combineLatest, skip } from 'rxjs';
import { GzRemediationActionWrapperWebComponent } from '../../common/remediationAction/component/GzRemediationActionWebComponent';
import { GzFileSizesWebComponent } from '../../common/fileSizes';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';
import { GzInfoBoxWebComponent } from '../../../../../../../ui/gzInfoBox';
import { classMap } from 'lit-html/directives/class-map.js';
import { SectionMask } from '../../../../redux/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_SANDBOX_ENDPOINT_SENSOR)
export class GzSandboxEndpointSensorWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  NetworkSandboxingModuleFormValue,
  SandboxEndpointSensorSearchableKeywords,
  ManualSubmissionApiClients
> {
  /**
   * Path for Sandbox Endpoint Sensor section.
   */
  protected path = {
    moduleId: ModuleId.SANDBOX,
    sectionId: SectionId.ENDPOINT_SENSOR,
  };

  /**
   * Searchable keywords for Sandbox Endpoint Sensor section.
   */
  protected searchableKeywords = getSandboxEndpointSensorSearchableKeywords();

  private remediationActionConfig = getRemediationActionConfigsByAnalysisMode();

  private isSandboxAnalyzerVisible = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SANDBOX_ANALYZER_VISIBLE]);
  private isSandboxLocalVisible = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SANDBOX_LOCAL_VISIBLE]);
  private isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  private isCloud = Application.Main.product.isCloud();

  @query('#is-cloud-sandbox-form-field')
  private isCloudSandboxFormField:
    | GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>
    | GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number | boolean>;

  @query('gz-policy-panel-sandbox-local-instance')
  private localSandboxInstance: GzLocalSandboxInstanceWebComponent;

  @query('gz-policy-panel-sandbox-proxy-configuration')
  private proxyConfiguration: GzProxyConfigurationWebComponent;

  @query('#automatic-submission-form-field')
  private automaticSubmissionFormField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#automatic-submission-info-text')
  private automaticSubmissionInfoText: GzTextWebComponent;
  @query('#automatic-submission-content')
  private automaticSubmissionContent: HTMLElement;
  @query('#automatic-submission-info-box')
  private automaticSubmissionInfoBox: GzInfoBoxWebComponent;

  @query('gz-policy-panel-sandbox-content-prefiltering')
  private contentPrefiltering: GzContentPrefilteringWebComponent;

  @query('#analysis-mode-form-field')
  private analysisModeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('#remediation-actions-form-field')
  private remediationActionsFormField: GzFormFieldWebComponent<GzRemediationActionWrapperWebComponent>;

  @query('#file-sizes-form-field')
  private fileSizesFormField: GzFormFieldWebComponent<GzFileSizesWebComponent>;

  @query('gz-policy-panel-sandbox-detonation-profile')
  private detonationProfileComponent: GzDetonationProfileWebComponent;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  protected setMask(masks: SectionMask[]) {
    // for antimalware on access mask, only a part from this section is disabled
    super.setMask(this.masks.filter(mask => mask !== SectionMask.ANTIMALWARE_ON_ACCESS));
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    const titleLabel = this.isCloud
      ? this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.SandboxAnalyzer]
      : this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.EndpointSensor];

    return html`<gz-title class="content" searchable-label="${titleLabel.label}"
      ><span>${titleLabel.translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="sandbox-endpoint-sensor-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'Provides a powerful layer of protection against advanced threats by performing automatic, in-depth analysis of suspicious objects.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div>
        <gz-title
          searchable-label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.ConnectionSettings]
            .label}"
          class="module-subtitle"
        >
          ${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.ConnectionSettings].translatedLabel}
        </gz-title>

        ${this.renderSandboxAnalyzerInstanceFields()} ${this.renderProxyConfiguration()}
      </div>

      ${this.renderAutomaticSubmission()}
    `;
  }

  private renderProxyConfiguration(): TemplateResult {
    return html` <gz-policy-panel-sandbox-proxy-configuration
      .proxyConfigurationLabel="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.ProxyConfiguration]}"
    ></gz-policy-panel-sandbox-proxy-configuration>`;
  }

  private renderAutomaticSubmission(): TemplateResult {
    return html`
      <gz-form-field id="automatic-submission-form-field" class="mt-4 mbw-1" status-field>
        <gz-toggle
          class="toggle-subtitle"
          gz-form-input
          searchable-label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.AutomaticSubmission]
            .label}"
          @toggleChange="${this.toggleAutomaticSubmissionDisabledState.bind(this)}"
        >
          <span
            >${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.AutomaticSubmission]
              .translatedLabel}</span
          >
        </gz-toggle>
      </gz-form-field>
      <gz-text fontSize="12" fontWeight="400" class="infoText" id="automatic-submission-info-text"
        >${translateFn(
          'Enable the integrated endpoint sensor to submit samples containing suspicious objects to Sandbox Analyzer for in-depth behavioral analysis.'
        )}
      </gz-text>

      <gz-info-box id="automatic-submission-info-box" severity="warning" class="mt-2">
        ${translateFn(
          'On-access scanning is disabled in the Antimalware section. Enable it before activating automatic sample submission.'
        )}
      </gz-info-box>

      <div id="automatic-submission-content">
        ${this.renderAnalysisMode()} ${this.renderRemediationActions()} ${this.renderContentPrefiltering()}
        ${this.renderFileSizes()} ${this.renderDetonationProfile()}
      </div>
    `;
  }

  toggleAutomaticSubmissionDisabledState() {
    // field not rendered yet
    if (!this.automaticSubmissionFormField) {
      return;
    }

    const isReadOnly = this.masks.includes(SectionMask.ANTIMALWARE_ON_ACCESS);

    toggleDisabled(this.automaticSubmissionFormField, isReadOnly);
    toggleDisabled(this.automaticSubmissionContent, isReadOnly || !this.automaticSubmissionFormField.value);
    toggleDisabled(this.automaticSubmissionInfoText, isReadOnly || !this.automaticSubmissionFormField.value);
    toggleVisibility(this.automaticSubmissionInfoBox, isReadOnly);
  }

  private renderAnalysisMode(): TemplateResult {
    const classes = classMap({
      hidden: this.isCompanionStandalone,
    });

    return html`<div class="${classes}">
      <gz-title
        searchable-label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.AnalysisMode].label}"
        fontSize="11"
        fontWeight="600"
        class="form-subtitle"
        >${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.AnalysisMode].translatedLabel}</gz-title
      >

      <gz-form-field id="analysis-mode-form-field" without-impact class="mt-2">
        <gz-radio-group
          gz-form-input
          class="d-flex flex-column"
          @radioSelectionChanged=${this.handleAnalysisModeChange}
        >
          <div class="mb-2">
            <gz-radio
              searchable-label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.Monitoring].label}"
              label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.Monitoring].translatedLabel}"
              .value=${AnalysisMode.MONITORING}
            ></gz-radio>
            <gz-text fontSize="12" fontWeight="400" class="helper-text"
              >${translateFn('Endpoint users can access objects during analysis.')}</gz-text
            >
          </div>

          <div>
            <gz-radio
              searchable-label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.Blocking].label}"
              label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.Blocking].translatedLabel}"
              .value=${AnalysisMode.BLOCKING}
            ></gz-radio>

            <gz-text fontSize="12" fontWeight="400" class="helper-text"
              >${translateFn(
                'Endpoint users can access objects only after the analysis confirms they are clean.'
              )}</gz-text
            >
          </div>
        </gz-radio-group>
      </gz-form-field>
    </div>`;
  }

  private renderRemediationActions(): TemplateResult {
    return html`
      <gz-form-field id="remediation-actions-form-field">
        <gz-policy-panel-sandbox-remediation-actions
          gz-form-input
          .remediationLabel="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.Remediation]}"
          .defaultLabel="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.DefaultAction]}"
          ?isDisabled="${this.isCompanionStandalone}"
        ></gz-policy-panel-sandbox-remediation-actions>
      </gz-form-field>
    `;
  }

  private renderContentPrefiltering(): TemplateResult {
    return html` <gz-policy-panel-sandbox-content-prefiltering
      .contentPrefilteringLabel="${this.searchableKeywords[
        SandboxEndpointSensorSearchableKeywords.ContentPrefiltering
      ]}"
      .exceptionsLabel="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.Exclusions]}"
    ></gz-policy-panel-sandbox-content-prefiltering>`;
  }

  private renderFileSizes(): TemplateResult {
    return html`
      <gz-form-field id="file-sizes-form-field">
        <gz-policy-panel-sandbox-file-sizes
          gz-form-input
          .sizeFilterLabel="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.DoNotSubmit]}"
        ></gz-policy-panel-sandbox-file-sizes>
      </gz-form-field>
    `;
  }

  private renderDetonationProfile(): TemplateResult {
    if (this.isSandboxAnalyzerVisible && this.isSandboxLocalVisible) {
      return html` <gz-policy-panel-sandbox-detonation-profile
        .detonationProfileLabel="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.DetonationProfile]}"
      ></gz-policy-panel-sandbox-detonation-profile>`;
    } else {
      return html``;
    }
  }

  private renderSandboxAnalyzerInstanceFields(): TemplateResult {
    if (this.isSandboxAnalyzerVisible && this.isSandboxLocalVisible) {
      return this.renderSandboxAnalyzerInstanceRadioButtons();
    } else {
      return this.renderSandboxAnalyzerInstanceCheckbox();
    }
  }

  private renderSandboxAnalyzerInstanceRadioButtons(): TemplateResult {
    return html`<div class="section">
      <gz-form-field id="is-cloud-sandbox-form-field">
        <gz-radio-group
          gz-form-input
          class="d-flex flex-column"
          @radioSelectionChanged=${e => {
            this.localSandboxInstance.disabled = e.detail.selectedValue;
            toggleVisibility(this.detonationProfileComponent, !e.detail.selectedValue);
          }}
        >
          <div class="mb-2">
            <gz-radio
              searchable-label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.CloudSandboxAnalyzer]
                .label}"
              label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.CloudSandboxAnalyzer]
                .translatedLabel}"
              .value=${true}
            ></gz-radio>
            <gz-text fontSize="12" fontWeight="400" class="form-field-info-text-connection-settings"
              >${translateFn(
                'The endpoint sensor will submit samples for detonation to the Sandbox Analyzer instances hosted by Bitdefender.'
              )}</gz-text
            >
          </div>

          <div>
            <gz-radio
              searchable-label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.LocalSandboxAnalyzer]
                .label}"
              label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.LocalSandboxAnalyzer]
                .translatedLabel}"
              .value=${false}
            ></gz-radio>

            <gz-text fontSize="12" fontWeight="400" class="form-field-info-text-connection-settings"
              >${translateFn(
                'Connect the endpoint sensor and the Sandbox Analyzer portal through a proxy server.'
              )}</gz-text
            >

            <gz-policy-panel-sandbox-local-instance class="mt-2"></gz-policy-panel-sandbox-local-instance>
          </div>
        </gz-radio-group>
      </gz-form-field>
    </div>`;
  }

  private renderSandboxAnalyzerInstanceCheckbox() {
    return html`
      <gz-form-field class="label-mw-250" id="is-cloud-sandbox-form-field" readonly>
        <gz-checkbox
          searchable-label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.CloudSandboxAnalyzer]
            .label}"
          label="${this.searchableKeywords[SandboxEndpointSensorSearchableKeywords.CloudSandboxAnalyzer]
            .translatedLabel}"
          gz-form-input
        ></gz-checkbox>
        <gz-text fontSize="12" fontWeight="400" class="form-field-info-text-connection-settings"
          >${translateFn(
            'The endpoint sensor will submit samples for detonation to the Sandbox Analyzer instances hosted by Bitdefender.'
          )}
        </gz-text>
      </gz-form-field>
    `;
  }

  private handleAnalysisModeChange(e: CustomEvent) {
    const analysisMode = e.detail.selectedValue;

    this.remediationActionsFormField.Input.defaultActionOptions =
      this.remediationActionConfig[analysisMode].defaultActions.options;

    // initial change is without impact for default and fallback actions
    // values are set in the next change
    // for the first change, values from backend are used
    if (this.analysisModeFormField.hasAttribute('without-impact')) {
      this.analysisModeFormField.removeAttribute('without-impact');
      return;
    }

    this.remediationActionsFormField.value = {
      defaultAction: this.remediationActionConfig[analysisMode].defaultActions.value,
    };
  }

  /**
   * Set clients.
   */
  protected setClients() {
    this.clients = PolicyDependenciesContainer.getInstance().resolveDependency(
      'ManualSubmission'
    ) as ManualSubmissionApiClients;

    if (this.localSandboxInstance) {
      this.localSandboxInstance.getLocalSandboxes = this.clients.getLocalSandboxes;
    }
  }

  /**
   * Subscribe to validity event received from form group and port section.
   */
  protected subscribeToValidityEvent() {
    // combine into a single observable (skip first value, because it is always true)
    const formGroupValidSubscription = combineLatest([this.formGroup.valid$, this.fileSizesFormField.Input.valid$])
      .pipe(skip(1))
      .subscribe({
        next: ([formGroupValid, fileSizesValid]) => {
          // wait for all values to be defined
          if (fileSizesValid === undefined) {
            return;
          }

          this.changeValidityIfNecessary(formGroupValid && fileSizesValid);
        },
      });

    this.subscriptions.push(formGroupValidSubscription);
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.networkSandboxing
   */
  protected getValueFromManager(): NetworkSandboxingModuleFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        networkSandboxing: getNetworkSandboxingDefaultValue(),
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        networkSandboxing: PolicyPanelManager.getInstance().getPolicy('uiSettings.networkSandboxing'),
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        networkSandboxing: {
          enabled: this.automaticSubmissionFormField,
          analysisMode: this.analysisModeFormField,
          remediationActions: this.remediationActionsFormField,
          settings: {
            isCloudSandbox: this.isCloudSandboxFormField,
            localSandbox: this.localSandboxInstance?.formGroup || new FormGroup({}),
            detonationProfile: this.detonationProfileComponent?.formGroup || new FormGroup({}),
          },
          proxy: this.proxyConfiguration.formGroup,
          contentPrefiltering: this.contentPrefiltering.formGroup,
          sizeFilter: this.fileSizesFormField,
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    if (this.localSandboxInstance) {
      this.localSandboxInstance.selectedOption = {
        title: getTitleForLocalSandbox(
          this.valueT.uiSettings.networkSandboxing.settings.hostname,
          this.valueT.uiSettings.networkSandboxing.settings.ip
        ),
        value: this.valueT.uiSettings.networkSandboxing.settings.endpointId || '',
      };
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        networkSandboxing: {
          enabled: this.valueT.uiSettings.networkSandboxing.enabled,
          analysisMode: this.valueT.uiSettings.networkSandboxing.analysisMode,
          remediationActions: {
            defaultAction: this.valueT.uiSettings.networkSandboxing.remediationActions.defaultAction,
          },
          settings: {
            isCloudSandbox: this.valueT.uiSettings.networkSandboxing.settings.isCloudSandbox,
            localSandbox: {
              endpointId: this.valueT.uiSettings.networkSandboxing.settings.endpointId,
            },
            detonationProfile: {
              value: this.valueT.uiSettings.networkSandboxing.settings.detonationProfile,
            },
          },
          proxy: this.valueT.uiSettings.networkSandboxing.proxy,
          contentPrefiltering: {
            categories: convertDbValueToFormValue(
              this.valueT.uiSettings.networkSandboxing.contentPrefiltering.categories
            ),
            excludeExtensions: this.valueT.uiSettings.networkSandboxing.contentPrefiltering.excludeExtensions,
          },
          sizeFilter: this.valueT.uiSettings.networkSandboxing.contentPrefiltering.sizeFilter,
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup || !this.valueT) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // save local sandbox
    const localSandboxFormValue = this.localSandboxInstance?.value;
    const localSandboxToSave = {
      endpointId: localSandboxFormValue
        ? localSandboxFormValue.endpointId
        : this.valueT.uiSettings.networkSandboxing.settings.endpointId,
      hostname: localSandboxFormValue
        ? localSandboxFormValue.name
        : this.valueT.uiSettings.networkSandboxing.settings.hostname,
      ip: localSandboxFormValue ? localSandboxFormValue.ip : this.valueT.uiSettings.networkSandboxing.settings.ip,
    };

    _.unset(value, ['uiSettings', 'networkSandboxing', 'settings', 'localSandbox']);
    _.set(value, ['uiSettings', 'networkSandboxing', 'settings', 'endpointId'], localSandboxToSave.endpointId);
    _.set(value, ['uiSettings', 'networkSandboxing', 'settings', 'hostname'], localSandboxToSave.hostname ?? '');
    _.set(value, ['uiSettings', 'networkSandboxing', 'settings', 'ip'], localSandboxToSave.ip ?? '');

    // save detonation profile
    const detonationProfile = _.get(
      value,
      ['uiSettings', 'networkSandboxing', 'settings', 'detonationProfile', 'value'],
      null
    );
    if (detonationProfile !== null) {
      _.set(value, ['uiSettings', 'networkSandboxing', 'settings', 'detonationProfile'], detonationProfile);
    } else {
      _.unset(value, ['uiSettings', 'networkSandboxing', 'settings', 'detonationProfile']);
    }

    // save categories
    const categories = _.get(value, ['uiSettings', 'networkSandboxing', 'contentPrefiltering', 'categories']);
    _.set(
      value,
      ['uiSettings', 'networkSandboxing', 'contentPrefiltering', 'categories'],
      convertFormValueToDbValue(categories)
    );

    // save file sizes
    const fileSizes = _.get(value, ['uiSettings', 'networkSandboxing', 'sizeFilter']);
    _.unset(value, ['uiSettings', 'networkSandboxing', 'sizeFilter']);
    _.set(value, ['uiSettings', 'networkSandboxing', 'contentPrefiltering', 'sizeFilter'], fileSizes);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'networkSandboxing'], {});
    }

    return value;
  }

  /**
   * Override reset method, because we need to reset also chips component.
   */
  public reset() {
    super.reset();
    this.contentPrefiltering.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_SANDBOX_ENDPOINT_SENSOR: GzSandboxEndpointSensorWebComponent;
  }
}
